import axios from 'axios';

/** Page navigation, timeout, or duplicate request; not worth alarming in the console. */
function isBenignVersionCheckError(error) {
  if (!error) return false;
  const msg = String(error.message || '');
  if (msg === 'Request aborted' || /aborted|cancel/i.test(msg)) return true;
  if (error.code === 'ERR_CANCELED' || error.code === 'ECONNABORTED') return true;
  if (error.name === 'CanceledError' || error.name === 'AbortError') return true;
  return false;
}

class VersionService {
  constructor() {
    this.currentVersion = null;
    this.checkInterval = null;
    this.listeners = [];
    this.isChecking = false;
    this.eventSource = null;
    this.sseBackoffMs = 2000;
    this.maxSseBackoffMs = 60000;
    
    // Auto-refresh configuration
    this.autoRefreshEnabled = process.env.NODE_ENV === 'production'; // Only auto-refresh in production
    this.autoRefreshDelay = 3000; // 3 seconds delay before auto-refresh
    
    // Check every 60s in production, 30s in development (SSE will handle near-realtime when available)
    this.checkIntervalMs = process.env.NODE_ENV === 'production' ? 60 * 1000 : 30 * 1000;
  }

  // Add listener for version updates
  addListener(callback) {
    this.listeners.push(callback);
  }

  // Remove listener
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  // Notify all listeners about version update
  notifyListeners(hasUpdate) {
    this.listeners.forEach(listener => {
      try {
        listener(hasUpdate);
      } catch (error) {
        console.error('Error in version update listener:', error);
      }
    });
  }

  // Enable/disable auto-refresh
  setAutoRefresh(enabled) {
    this.autoRefreshEnabled = enabled;
    console.log('🔄 Auto-refresh', enabled ? 'enabled' : 'disabled');
  }

  // Set auto-refresh delay
  setAutoRefreshDelay(delayMs) {
    this.autoRefreshDelay = delayMs;
    console.log('⏰ Auto-refresh delay set to', delayMs, 'ms');
  }

  // Check for version updates
  async checkForUpdates() {
    if (this.isChecking) {
      return;
    }

    this.isChecking = true;

    try {
      console.log('🔍 Checking for version updates...');
      const response = await axios.get('/api/version', {
        timeout: 10000, // 10 second timeout
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      console.log('📡 Version API response:', response.data);

      if (response.data && response.data.success) {
        const serverVersion = response.data.version;
        const serverBuildTime = response.data.buildTime;
        
        // If this is the first check, store the current version
        if (!this.currentVersion) {
          this.currentVersion = {
            version: serverVersion,
            buildTime: serverBuildTime,
            timestamp: response.data.timestamp
          };
          console.log('🔍 Initial version check:', this.currentVersion);
          return;
        }

        // Check if version has changed
        const hasVersionUpdate = this.currentVersion.version !== serverVersion;
        const hasBuildTimeUpdate = this.currentVersion.buildTime !== serverBuildTime;
        
        console.log('🔍 Version comparison:', {
          currentVersion: this.currentVersion.version,
          serverVersion: serverVersion,
          hasVersionUpdate: hasVersionUpdate,
          currentBuildTime: this.currentVersion.buildTime,
          serverBuildTime: serverBuildTime,
          hasBuildTimeUpdate: hasBuildTimeUpdate
        });
        
        if (hasVersionUpdate || hasBuildTimeUpdate) {
          console.log('🔄 Version update detected!', {
            current: this.currentVersion,
            server: { version: serverVersion, buildTime: serverBuildTime }
          });
          
          // Update current version
          this.currentVersion = {
            version: serverVersion,
            buildTime: serverBuildTime,
            timestamp: response.data.timestamp
          };
          
          // Notify listeners
          this.notifyListeners(true);
          
          // Auto-refresh if enabled
          if (this.autoRefreshEnabled) {
            console.log(`🔄 Auto-refreshing in ${this.autoRefreshDelay}ms...`);
            setTimeout(() => {
              this.forceRefresh();
            }, this.autoRefreshDelay);
          }
        } else {
          console.log('✅ No version update detected');
        }
      } else {
        console.log('⚠️ Invalid response from version API:', response.data);
      }
    } catch (error) {
      if (isBenignVersionCheckError(error)) {
        return;
      }
      console.error('❌ Error checking for version updates:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      // Don't notify listeners on error to avoid false positives
    } finally {
      this.isChecking = false;
    }
  }

  // Start periodic version checking
  startVersionChecking() {
    if (this.checkInterval) {
      return; // Already started
    }

    console.log('🚀 Starting version checking service');
    // Attempt SSE first for push-style updates
    this.startSSE();

    // Initial check
    this.checkForUpdates();
    
    // Set up periodic checking
    this.checkInterval = setInterval(() => {
      this.checkForUpdates();
    }, this.checkIntervalMs);
  }

  // Stop version checking
  stopVersionChecking() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      console.log('🛑 Stopped version checking service');
    }
  }

  // Get current version info
  getCurrentVersion() {
    return this.currentVersion;
  }

  // Force refresh the page
  forceRefresh() {
    console.log('🔄 Force refreshing page for update');
    // Hard navigation with cache-busting to avoid stale HTML caches
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('v', Date.now().toString());
      window.location.href = url.toString();
    } catch (e) {
      window.location.reload();
    }
  }

  // Start Server-Sent Events stream
  startSSE() {
    if (!('EventSource' in window)) {
      console.log('ℹ️ EventSource not supported, using polling only');
      return;
    }
    if (this.eventSource) {
      return;
    }
    // Many production proxies (Render, nginx, CDNs) do not support long-lived SSE well;
    // that yields browser "Can't establish a connection" noise. Polling (checkIntervalMs)
    // is enough. Opt in: REACT_APP_ENABLE_VERSION_SSE=true
    const sseEnabled =
      process.env.REACT_APP_ENABLE_VERSION_SSE === 'true' ||
      (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_VERSION_SSE !== 'false');
    if (!sseEnabled) {
      console.log('ℹ️ Version SSE disabled; using polling only (set REACT_APP_ENABLE_VERSION_SSE=true to enable).');
      return;
    }
    const connect = () => {
      console.log('🔌 Connecting to version SSE stream...');
      try {
        this.eventSource = new EventSource('/api/version/stream', { withCredentials: false });

        this.eventSource.addEventListener('version', (evt) => {
          try {
            const data = JSON.parse(evt.data);
            if (!data) return;

            // Initialize currentVersion if not set
            if (!this.currentVersion) {
              this.currentVersion = {
                version: data.version,
                buildTime: data.buildTime,
                timestamp: data.timestamp,
              };
              console.log('🔍 Initial version (SSE):', this.currentVersion);
              return;
            }

            const hasUpdate = this.currentVersion.version !== data.version || this.currentVersion.buildTime !== data.buildTime;
            if (hasUpdate) {
              console.log('📣 SSE version update detected:', { from: this.currentVersion, to: data });
              this.currentVersion = {
                version: data.version,
                buildTime: data.buildTime,
                timestamp: data.timestamp,
              };
              this.notifyListeners(true);
              if (this.autoRefreshEnabled) {
                setTimeout(() => this.forceRefresh(), this.autoRefreshDelay);
              }
            }
          } catch (e) {
            console.log('⚠️ Error handling SSE data:', e);
          }
        });

        this.eventSource.onerror = () => {
          console.log('⚠️ SSE connection error; will retry with backoff');
          this.eventSource.close();
          this.eventSource = null;
          setTimeout(() => {
            this.sseBackoffMs = Math.min(this.sseBackoffMs * 2, this.maxSseBackoffMs);
            connect();
          }, this.sseBackoffMs);
        };

        this.eventSource.onopen = () => {
          console.log('✅ SSE connected');
          this.sseBackoffMs = 2000;
        };
      } catch (e) {
        console.log('⚠️ Failed to start SSE, will fallback to polling only', e);
      }
    };
    connect();
  }
}

// Create singleton instance
const versionService = new VersionService();

export default versionService;
